import { baseApi, API_BASE_URL } from './baseApi';

/**
 * Zendesk Contacts API endpoints
 * Handles syncing contacts from Zendesk Support and Zendesk Sell
 */
export const zendeskContactsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /zendesk-contacts - Get contacts with pagination, search, filtering, and sorting
    getContacts: builder.query({
      query: ({ page = 1, limit = 10, search, linked, owner, customer_status, prospect_status, sort_by, sort_direction } = {}) => {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', limit);
        if (search) params.append('search', search);
        if (linked !== undefined && linked !== null && linked !== '') params.append('linked', linked);
        if (owner) params.append('owner', owner);
        if (customer_status) params.append('customer_status', customer_status);
        if (prospect_status) params.append('prospect_status', prospect_status);
        if (sort_by) params.append('sort_by', sort_by);
        if (sort_direction) params.append('sort_direction', sort_direction);
        return `/zendesk-contacts?${params.toString()}`;
      },
      providesTags: (result) =>
        result?.contacts
          ? [
              ...result.contacts.map(({ id }) => ({ type: 'ZendeskContact', id })),
              { type: 'ZendeskContact', id: 'LIST' },
            ]
          : [{ type: 'ZendeskContact', id: 'LIST' }],
      transformResponse: (response) => {
        return {
          contacts: response?.contacts || [],
          pagination: response?.pagination || {
            current_page: 1,
            per_page: (response?.contacts || []).length,
            total: (response?.contacts || []).length,
            total_pages: 1
          },
          meta: response?.meta || {},
        };
      },
    }),

    // GET /zendesk-contacts/stats - Get contact statistics
    getContactStats: builder.query({
      query: () => '/zendesk-contacts/stats',
      providesTags: [{ type: 'ZendeskContact', id: 'STATS' }],
    }),

    // GET /zendesk-contacts/unlinked - Get unlinked contacts
    getUnlinkedContacts: builder.query({
      query: () => '/zendesk-contacts/unlinked',
      providesTags: [{ type: 'ZendeskContact', id: 'UNLINKED' }],
    }),

    // POST /zendesk-contacts/import - Import Sell contacts from CSV
    // Using custom queryFn for proper FormData handling
    importSellContacts: builder.mutation({
      queryFn: async (formData) => {
        try {
          const token = localStorage.getItem('token');
          // Remove trailing slash from API_BASE_URL to avoid double slashes
          const baseUrl = API_BASE_URL.replace(/\/$/, '');
          const response = await fetch(`${baseUrl}/zendesk-contacts/import`, {
            method: 'POST',
            headers: {
              'X-Auth-Token': token,
              'Authorization': `Bearer ${token}`,
              // Don't set Content-Type - browser will set it with boundary for FormData
            },
            body: formData,
          });
          
          const data = await response.json();
          
          if (!response.ok) {
            return { error: { status: response.status, data } };
          }
          
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      invalidatesTags: [
        { type: 'ZendeskContact', id: 'LIST' },
        { type: 'ZendeskContact', id: 'STATS' },
        { type: 'ZendeskContact', id: 'UNLINKED' },
      ],
    }),

    // POST /zendesk-contacts/sync-support - Sync Support users
    syncSupportUsers: builder.mutation({
      query: (options = {}) => ({
        url: '/zendesk-contacts/sync-support',
        method: 'POST',
        body: options,
      }),
      invalidatesTags: [
        { type: 'ZendeskContact', id: 'LIST' },
        { type: 'ZendeskContact', id: 'STATS' },
        { type: 'ZendeskContact', id: 'UNLINKED' },
      ],
    }),

    // POST /zendesk-contacts/sync - Sync Sell contacts from API (incremental, newest first)
    syncSellContacts: builder.mutation({
      query: (options = {}) => ({
        url: '/zendesk-contacts/sync',
        method: 'POST',
        body: options,
      }),
      invalidatesTags: [
        { type: 'ZendeskContact', id: 'LIST' },
        { type: 'ZendeskContact', id: 'STATS' },
        { type: 'ZendeskContact', id: 'UNLINKED' },
        { type: 'Person', id: 'LIST' },
      ],
    }),

    // POST /zendesk-contacts/:id/link - Link contact to person
    linkContactToPerson: builder.mutation({
      query: ({ contactId, personId }) => ({
        url: `/zendesk-contacts/${contactId}/link`,
        method: 'POST',
        body: { person_id: personId },
      }),
      invalidatesTags: [
        { type: 'ZendeskContact', id: 'LIST' },
        { type: 'ZendeskContact', id: 'STATS' },
        { type: 'ZendeskContact', id: 'UNLINKED' },
      ],
    }),

    // POST /zendesk-contacts/:id/auto-match - Auto-match contact to person
    autoMatchContact: builder.mutation({
      query: (contactId) => ({
        url: `/zendesk-contacts/${contactId}/auto-match`,
        method: 'POST',
      }),
      invalidatesTags: [
        { type: 'ZendeskContact', id: 'LIST' },
        { type: 'ZendeskContact', id: 'STATS' },
        { type: 'ZendeskContact', id: 'UNLINKED' },
      ],
    }),

    // POST /zendesk-contacts/auto-create-all - Auto-match or create all unlinked
    autoCreateAllUnlinked: builder.mutation({
      query: (options = {}) => ({
        url: '/zendesk-contacts/auto-create-all',
        method: 'POST',
        body: options,
      }),
      invalidatesTags: [
        { type: 'ZendeskContact', id: 'LIST' },
        { type: 'ZendeskContact', id: 'STATS' },
        { type: 'ZendeskContact', id: 'UNLINKED' },
        { type: 'Person', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetContactsQuery,
  useGetContactStatsQuery,
  useGetUnlinkedContactsQuery,
  useImportSellContactsMutation,
  useSyncSupportUsersMutation,
  useSyncSellContactsMutation,
  useLinkContactToPersonMutation,
  useAutoMatchContactMutation,
  useAutoCreateAllUnlinkedMutation,
} = zendeskContactsApi;
